import { expressionsJson } from "@corca/data";

import { functions } from "@/components/suggestions/providers/functionSuggestionsProvider";
import { suggestionToNodeJson } from "@/components/suggestions/providers/utils/suggestionToNodeJson";
import { insertNode } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { nodeFromJson } from "@/models/nodes/nodeFromJson";
import { PlaceholderNodeModel } from "@/models/nodes/PlaceholderNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data";

export type WrapTemplate = {
  nodesJson: NodeJson[];
  targetNode?: { index: number; path?: string };
  bodyPath?: string;
};

export function functionWrapTemplate(functionKey: string): WrapTemplate | undefined {
  const suggestion = functions.find((s) => s.item.key === functionKey);
  if (!suggestion) {
    return undefined;
  }

  const node = suggestionToNodeJson(suggestion);
  if (node?.type !== "function") {
    return undefined;
  }

  const symbolNode = node.children?.find((child) => child.type === "symbol");
  const hasLabels = symbolNode?.children?.some((child) => child.type === "label") ?? false;
  const targetNode = hasLabels
    ? {
        index: 0,
        path: "function:0::symbol:0::label:0::inline:0",
      }
    : undefined;

  return {
    nodesJson: [node],
    bodyPath: "function:0::inline:1",
    targetNode,
  };
}

export function expressionWrapTemplate(name: string): WrapTemplate | undefined {
  const expression = expressionsJson.find((e) => e.name === name);
  if (!expression?.nodesJson) {
    return undefined;
  }

  return {
    nodesJson: expression.nodesJson,
    targetNode: expression.targetNode,
  };
}

function findPlaceholder(nodes: NodeModel[]): PlaceholderNodeModel | undefined {
  for (const node of nodes) {
    if (node.is(PlaceholderNodeModel)) {
      return node;
    }

    const found = findPlaceholder(node.childrenPlease);
    if (found) return found;
  }

  return undefined;
}

function resolveTargetNode(
  nodes: NodeModel[],
  targetNode: { index: number; path?: string },
): NodeModel | undefined {
  if (!targetNode.path) {
    return nodes[targetNode.index];
  }

  let current: NodeModel | undefined;

  for (const segment of targetNode.path.split("::")) {
    const [type, indexStr] = segment.split(":");
    const candidates = current?.childrenPlease ?? nodes;
    const candidate = candidates[parseInt(indexStr, 10)];

    if (!candidate || candidate.type !== type) {
      return undefined;
    }

    current = candidate;
  }

  return current;
}

export function applyWrapTemplate(
  doc: DocumentModel,
  template: WrapTemplate,
  nodes: NodeModel[],
): boolean {
  const { cursor, selection } = doc;

  if (!cursorIsActive(cursor) || nodes.length === 0 || template.nodesJson.length === 0) {
    return false;
  }

  doc.transact(() => {
    cursor.moveAfterNode(nodes.at(-1));

    const inserted = doc.batchCreateNodes(() =>
      template.nodesJson.map((json) => nodeFromJson(doc, json, { resetIds: true })).filter(Boolean),
    );

    for (const node of inserted) {
      insertNode(cursor, node);
      cursor.moveAfterNode(node);
    }

    const target = template.targetNode
      ? resolveTargetNode(inserted, template.targetNode)
      : undefined;
    const placeholder = findPlaceholder(inserted);

    if (placeholder) {
      nodes.forEach((node) => placeholder.insertSiblingBefore(node));
      doc.removeNode(placeholder);
    } else if (template.bodyPath) {
      const body = resolveTargetNode(inserted, { index: 0, path: template.bodyPath });

      if (body) {
        body.appendChildren(nodes);

        if (!target) {
          cursor.moveTo(body, body.nodes.length);
        }
      }
    }

    if (target) {
      cursor.moveTo(target, template.targetNode?.index ?? 0);
    }

    selection.resetSelection();
  }, "applyWrapTemplate");

  return true;
}
